/**
 * Profile Service
 * Handles user profile operations with Supabase
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger';
import type { Id } from '@/lib/types';

export interface Profile {
  id: Id;
  email?: string | null;
  displayName?: string | null;
  fullName?: string | null;
  avatarUrl?: string | null;
  phone?: string | null;
  isActive?: boolean;
  locale?: string;
  timezone?: string;
  geoPlaceId?: string | null;
}

export interface ProfileCity {
  id: string;
  name: string;
  region_name?: string | null;
}

export const profileService = {
  /**
   * Get profile by user ID
   */
  async getProfile(userId: Id): Promise<{ data: Profile | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, display_name, full_name, avatar_url, phone, is_active, locale, timezone, geo_place_id')
        .eq('id', userId)
        .single();

      if (error) {
        return { data: null, error };
      }

      return {
        data: {
          id: data.id,
          email: data.email,
          displayName: data.display_name,
          fullName: data.full_name,
          avatarUrl: data.avatar_url,
          phone: data.phone,
          isActive: data.is_active,
          locale: data.locale,
          timezone: data.timezone,
          geoPlaceId: data.geo_place_id,
        },
        error: null,
      };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error('Failed to get profile') };
    }
  },

  /**
   * Get user's saved city with details
   */
  async getProfileCity(userId: Id): Promise<{ data: ProfileCity | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('geo_place_id')
        .eq('id', userId)
        .single();

      if (error || !data?.geo_place_id) {
        return { data: null, error };
      }

      // Get city details
      const { data: cityData, error: cityError } = await supabase
        .rpc('get_cities', {
          p_country_code: 'RU',
          p_query: null,
          p_limit: 1,
          p_offset: 0,
          p_include_inactive: false,
          p_include_other: true,
          p_require_coords: false,
        });

      // Filter to find the matching city by ID
      const city = (cityData as any[])?.find(c => c.id === data.geo_place_id);
      
      if (city) {
        return {
          data: {
            id: city.id,
            name: city.name,
            region_name: city.region_name,
          },
          error: null,
        };
      }

      // Fallback: fetch from geo_cities view directly
      const { data: geoCity, error: geoCityError } = await supabase
        .from('geo_cities')
        .select('id, name, region_name')
        .eq('id', data.geo_place_id)
        .single();

      if (geoCityError || !geoCity) {
        return { data: null, error: geoCityError };
      }

      return {
        data: {
          id: geoCity.id,
          name: geoCity.name,
          region_name: geoCity.region_name,
        },
        error: null,
      };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error('Failed to get profile city') };
    }
  },

  /**
   * Update profile
   */
  async updateProfile(userId: Id, updates: Partial<Profile>): Promise<{ data: Profile | null; error: Error | null }> {
    try {
      // Map Profile fields to database columns
      const dbUpdates: Record<string, unknown> = {};
      if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
      if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName;
      if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.geoPlaceId !== undefined) dbUpdates.geo_place_id = updates.geoPlaceId;

      const { data, error } = await supabase
        .from('users')
        .update(dbUpdates)
        .eq('id', userId)
        .select('id, email, display_name, full_name, avatar_url, phone, is_active, locale, timezone, geo_place_id')
        .single();

      if (error) {
        return { data: null, error };
      }

      return {
        data: {
          id: data.id,
          email: data.email,
          displayName: data.display_name,
          fullName: data.full_name,
          avatarUrl: data.avatar_url,
          phone: data.phone,
          isActive: data.is_active,
          locale: data.locale,
          timezone: data.timezone,
          geoPlaceId: data.geo_place_id,
        },
        error: null,
      };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error('Failed to update profile') };
    }
  },

  /**
   * Save city to user profile
   */
  async saveCity(userId: Id, cityId: string): Promise<{ success: boolean; error: Error | null }> {
    try {
      logger.debug('Saving city', { context: { feature: 'profile' }, data: { cityId }, allowList: ['cityId'] });
      
      const { data, error } = await supabase
        .from('users')
        .update({ geo_place_id: cityId })
        .eq('id', userId)
        .select('id, geo_place_id')
        .single();

      if (error) {
        logger.warn('Error saving city', { error, context: { feature: 'profile' } });
        return { success: false, error };
      }

      logger.debug('City saved successfully', { context: { feature: 'profile' } });
      return { success: true, error: null };
    } catch (e) {
      logger.warn('Exception saving city', { error: e, context: { feature: 'profile' } });
      return { success: false, error: e instanceof Error ? e : new Error('Failed to save city') };
    }
  },

  /**
   * Upload avatar image
   */
  async uploadAvatar(userId: Id, file: File): Promise<{ url: string | null; error: Error | null }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        return { url: null, error: uploadError };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update user's avatar_url
      await supabase
        .from('users')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);

      return { url: publicUrl, error: null };
    } catch (e) {
      return { url: null, error: e instanceof Error ? e : new Error('Failed to upload avatar') };
    }
  },
};

export default profileService;
